/**
 * **Los permisos que resuelve la ruta y la pantalla recibe como `prop`, vueltos a preguntar en
 * cada entrada.**
 *
 * ## El fallo que cierra
 *
 * Las pantallas que deciden qué enseñar con los permisos entran por `conUsuarioActualizado` (#65):
 * la ruta espera al `Usuario.me()` de esta carga, pregunta a `permisos.js` y le pasa a la pantalla
 * la respuesta ya resuelta, para que no la decida ella al pintarse con el menú de la sesión
 * anterior. Eso es correcto **la primera vez**.
 *
 * Pero esas rutas llevan además `keepAlive`, y con `keepAlive` Framework7 **reutiliza la instancia**
 * que montó la primera resolución en lugar de crear otra. El `async` de la ruta se vuelve a
 * ejecutar en cada entrada —y la barrera con él—, pero los `props` que devuelve ya no llegan a
 * ninguna parte: el componente conserva los de entonces. Así, al cerrar sesión y entrar con otra
 * cuenta, la pantalla seguía decidiendo con el permiso de la cuenta ANTERIOR: a quien pasaba de
 * ADMIN/DIRECTOR a otro rol le seguía saliendo el botón —que el servidor acabaría rechazando con un
 * 403, que es justo lo que §9 no quiere que vea nadie— y, al revés, quien entraba con permiso no lo
 * veía hasta recargar la aplicación entera (revisión CODEX del PR #1737, r4303373384).
 *
 * Lo mismo valía para el enlace a la ficha del Mapa de aprobación, que nadie había señalado: son
 * las dos únicas rutas del repositorio con `keepAlive` y un permiso por `prop`, y de que no aparezca
 * una tercera sin pasar por aquí responde `scripts/prueba-permisos-de-la-ruta.js`.
 *
 * ## Lo que hace
 *
 * El `prop` de la ruta es el valor **de arranque** —el de la resolución que montó la pantalla, que
 * es el bueno— y la pantalla trabaja con una copia propia, `<prop>Ahora`, que se vuelve a preguntar
 * en cada entrada. Preguntar ahí es seguro: `page:beforein` ocurre después de que la ruta haya
 * resuelto, o sea después de la barrera, así que `permisos.js` contesta con el usuario de quien
 * está delante y no con el de la sesión anterior.
 *
 * Y se pregunta a la **misma función** que la ruta, que es la que vive en `permisos.js`: con una
 * copia aquí, el punto de entrada y la pantalla podrían volver a contestar cosas distintas, que es
 * lo que esas piezas existen para impedir.
 *
 * ## Cómo se usa
 *
 *   mixins: [permisosDeLaRuta({ puedeAbrirFicha: PermisosEmpleados.abrirFicha })]
 *
 * y en el `page:beforein` de la pantalla, lo primero:
 *
 *   this.revisarPermisosDeLaRuta();
 *
 * En la plantilla se usa `puedeAbrirFichaAhora`, no el `prop`. El `prop` sigue declarándolo este
 * mixin para que la ruta pueda pasarlo y para que la primera pintura —antes del primer
 * `page:beforein`— ya sea la correcta.
 *
 * @param preguntas objeto `{ <nombre del prop>: función que lo contesta }`
 * @return un mixin de Vue
 */
export default function permisosDeLaRuta(preguntas) {
    const nombres = Object.keys(preguntas);
    const props = {};
    nombres.forEach(function(nombre) {
        props[nombre] = { type: Boolean, default: false };
    });
    return {
        props: props,
        data: function() {
            const self = this;
            const estado = {};
            nombres.forEach(function(nombre) {
                estado[nombre + 'Ahora'] = self[nombre];
            });
            return estado;
        },
        methods: {
            /**
             * Vuelve a preguntarlos todos. Los TODOS no es cosmético: con una pantalla que decida
             * dos cosas, revisar solo la que se recuerde deja la otra con el permiso de la sesión
             * anterior, que es el mismo fallo en pequeño.
             */
            revisarPermisosDeLaRuta: function() {
                const self = this;
                nombres.forEach(function(nombre) {
                    self[nombre + 'Ahora'] = !!preguntas[nombre]();
                });
            }
        }
    };
}
